import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/* The rendered width of an element, kept current as it resizes — what an SVG chart draws against so
 * its strokes stay crisp at any column width rather than being scaled by a viewBox.
 *
 * Starts at a fallback so the first paint has geometry, and keeps the last real measurement when a
 * hidden ancestor reports zero: a chart in a closed tab must not collapse to nothing and then
 * re-animate when the tab opens. Without `ResizeObserver` (old engines, jsdom) the first measurement
 * stands. */
export function useMeasuredWidth<T extends HTMLElement>(
  fallback = 600,
): readonly [RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const node = ref.current;
    if (node === null) return undefined;
    const measure = (): void => {
      const measured = node.getBoundingClientRect().width;
      if (measured > 0) setWidth(measured);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, []);
  return [ref, width] as const;
}
